/**
 * Blog Load More
 *
 * Two responsibilities:
 * 1. "Load More" button – appends the next page of articles via AJAX and
 *    updates the URL (?page=N).
 * 2. Page-load init – if the user lands directly on ?page=N (N > 1), fetch
 *    pages 1 … N-1 and prepend them so the full history is visible.
 *
 * Multi-tag filtering is handled entirely by jkl-blog-category-filter.liquid.
 * When multi-tag mode is active the Load More button is hidden, so this file
 * only ever deals with single-tag or unfiltered pagination.
 *
 * Both the "Load More" click handler and the page-load init use the
 * Shopify Section Rendering API (?sections=<id>, JSON response) for
 * reliable section HTML extraction, consistent with jkl-blog-category-filter.
 */

// ---------------------------------------------------------------------------
// Resolve the section ID from the DOM.
// ---------------------------------------------------------------------------
function getSectionId() {
  return document.querySelector('.shopify-section--jkl-main-blog')
    ?.id.replace('shopify-section-', '');
}

// ---------------------------------------------------------------------------
// Build a URL for a given page number.
// Preserves the current pathname (which may include /tagged/tag) and any
// existing query params except `page`.
// ---------------------------------------------------------------------------
function buildPageUrl(pageNumber) {
  const url = new URL(window.location.href);
  if (pageNumber > 1) {
    url.searchParams.set('page', pageNumber);
  } else {
    url.searchParams.delete('page');
  }
  return url.toString();
}

// ---------------------------------------------------------------------------
// Fetch a page via the Section Rendering API and return article nodes +
// the next-page URL from the load-more button (if any).
// ---------------------------------------------------------------------------
async function fetchSectionPage(pageUrl, sectionId) {
  const sep      = pageUrl.includes('?') ? '&' : '?';
  const response = await fetch(`${pageUrl}${sep}sections=${sectionId}`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const json = await response.json();
  const html = json[sectionId];
  if (!html) throw new Error(`Section "${sectionId}" not in response`);

  const doc      = new DOMParser().parseFromString(html, 'text/html');
  const articles = Array.from(doc.querySelectorAll('.blog-posts > *'));
  const btn      = doc.querySelector('.blog-load-more-button');
  const nextUrl  = (btn?.getAttribute('data-next-url') || '').trim();

  return { articles, nextUrl };
}

// ---------------------------------------------------------------------------
// On page load: if ?page=N, prepend pages 1 … N-1
// ---------------------------------------------------------------------------
async function loadPreviousPagesOnInit() {
  const currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
  if (currentPage <= 1) return;

  const postsEl   = document.querySelector('.blog-posts');
  const sectionId = getSectionId();
  if (!postsEl || !sectionId) return;

  const button  = document.querySelector('.blog-load-more-button');
  const overlay = createOverlay(postsEl);
  if (button) button.style.visibility = 'hidden';

  try {
    // Fetch pages 1 … currentPage-1 sequentially
    const groups = [];
    for (let p = 1; p < currentPage; p++) {
      const { articles } = await fetchSectionPage(buildPageUrl(p), sectionId);
      groups.push(articles);
    }

    // Prepend in reverse so final order is page1 | page2 | … | currentPage
    for (let i = groups.length - 1; i >= 0; i--) {
      groups[i].slice().reverse().forEach(node => {
        postsEl.insertBefore(node.cloneNode(true), postsEl.firstChild);
      });
    }
  } catch {
    // Silently fail – user still sees the current page's articles
  } finally {
    overlay.remove();
    if (button) button.style.visibility = 'visible';
  }
}

function createOverlay(postsEl) {
  postsEl.parentElement.style.position = 'relative';
  const el = document.createElement('div');
  el.className = 'blog-loading-overlay';
  el.innerHTML = '<div class="blog-loading-spinner">Loading articles...</div>';
  postsEl.parentElement.appendChild(el);
  return el;
}

// ---------------------------------------------------------------------------
// Custom element – handles the "Load More" button click
// ---------------------------------------------------------------------------
class BlogLoadMore extends HTMLElement {
  connectedCallback() {
    this.postsEl = document.querySelector('.blog-posts');
    this.btn     = this.querySelector('.blog-load-more-button');
    if (this.btn) {
      this.btn.dataset.originalText = this.btn.textContent.trim();
      this.btn.addEventListener('click', this._handleClick.bind(this));
    }
  }

  async _handleClick(e) {
    e.preventDefault();
    if (!this.btn || this.btn.disabled) return;

    const nextUrl     = this.btn.getAttribute('data-next-url');
    const currentPage = parseInt(this.btn.getAttribute('data-current-page'), 10) || 1;
    const nextPage    = currentPage + 1;
    const origText    = this.btn.dataset.originalText;
    const sectionId   = getSectionId();

    if (!nextUrl || !sectionId) return;

    this.btn.disabled    = true;
    this.btn.textContent = 'LOADING...';

    try {
      const { articles, nextUrl: furtherUrl } =
        await fetchSectionPage(nextUrl, sectionId);

      // Append new articles
      articles.forEach(node => {
        this.postsEl?.appendChild(node.cloneNode(true));
      });

      // Update URL
      window.history.pushState({ page: nextPage }, '', buildPageUrl(nextPage));
      this.btn.setAttribute('data-current-page', nextPage);

      // Is there yet another page?
      if (furtherUrl) {
        this.btn.setAttribute('data-next-url', furtherUrl);
      } else {
        this.style.display = 'none'; // no more pages
      }
    } catch {
      this.btn.textContent = 'ERROR – TRY AGAIN';
      setTimeout(() => { this.btn.textContent = origText; }, 2000);
      return;
    } finally {
      this.btn.disabled = false;
      if (this.style.display !== 'none') this.btn.textContent = origText;
    }
  }
}

customElements.define('blog-load-more', BlogLoadMore);

// Run immediately (script is defer'd, so DOM is ready)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', loadPreviousPagesOnInit);
} else {
  loadPreviousPagesOnInit();
}
